import React from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Table,
  Dropdown,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from "reactstrap";
import { Link } from "react-router-dom";
import IntlMessages from "../../../helpers/IntlMessages";
import TemplateWarningCard from "../../../components/common/TemplateWarningCard";
import { Pagination } from "../general";
import ExpandableDataCardList from "../../../components/common/ExpandableDataCardList";
import StockDashboardMobile from "./StockDashboardMobile";
import {
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE,
  DASHBOARD_TOOLBAR_DANGER_BUTTON_STYLE,
  DASHBOARD_TOOLBAR_GHOST_BUTTON_STYLE,
  DASHBOARD_TOOLBAR_SELECT_STYLE,
  DASHBOARD_TOOLBAR_SOFT_ACTION_BUTTON_STYLE,
  hasAnyActiveFilter,
} from "../../../components/common/dashboardUx";
// LEGACY_OVERVIEW_DONUT_IMPORT_START
// import Doughnut from "../../../components/charts/Doughnut";
// LEGACY_OVERVIEW_DONUT_IMPORT_END

const STATUS_COLOR = {
  in_stock: "success",
  low_stock: "warning",
  out_of_stock: "danger",
  no_cost: "secondary",
  inactive: "light",
};

const STATUS_LABEL = {
  in_stock: "stock.dashboard.status.in-stock",
  low_stock: "stock.dashboard.status.low-stock",
  out_of_stock: "stock.dashboard.status.out-of-stock",
  no_cost: "stock.dashboard.status.no-cost",
  inactive: "stock.dashboard.status.inactive",
};

/* LEGACY_STOCK_TOOLBAR_LOCAL_STYLE_START
const STOCK_TOOLBAR_GHOST_BUTTON_STYLE = {
  color: "#4f5d6b",
  borderColor: "#d7dde5",
  backgroundColor: "#fff",
  boxShadow: "none",
};

const STOCK_TOOLBAR_SOFT_ACTION_BUTTON_STYLE = {
  color: "#1f3b57",
  borderColor: "transparent",
  backgroundColor: "#edf4fb",
  boxShadow: "none",
};

const STOCK_TOOLBAR_DANGER_BUTTON_STYLE = {
  color: "#d92550",
  borderColor: "#f3b8c4",
  backgroundColor: "#fff5f7",
  boxShadow: "none",
};

const STOCK_TOOLBAR_CONTROL_HEIGHT_STYLE = {
  height: 34,
  minHeight: 34,
  display: "inline-flex",
  alignItems: "center",
};

const STOCK_TOOLBAR_SELECT_STYLE = {
  height: 34,
  minHeight: 34,
  paddingTop: 0,
  paddingBottom: 0,
};
LEGACY_STOCK_TOOLBAR_LOCAL_STYLE_END */

const STOCK_TOOLBAR_GHOST_BUTTON_STYLE = DASHBOARD_TOOLBAR_GHOST_BUTTON_STYLE;
const STOCK_TOOLBAR_SOFT_ACTION_BUTTON_STYLE =
  DASHBOARD_TOOLBAR_SOFT_ACTION_BUTTON_STYLE;
const STOCK_TOOLBAR_DANGER_BUTTON_STYLE = DASHBOARD_TOOLBAR_DANGER_BUTTON_STYLE;
const STOCK_TOOLBAR_CONTROL_HEIGHT_STYLE =
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT_STYLE;
const STOCK_TOOLBAR_SELECT_STYLE = DASHBOARD_TOOLBAR_SELECT_STYLE;

const STOCK_CARD_VISUAL_STYLE = {
  inactive: {
    borderColor: "#e2e8f0",
    borderHoverColor: "#cbd5e1",
    bgColor: "#f8fafc",
    hoverBgColor: "#f1f5f9",
    accentColor: "#94a3b8",
    titleColor: "#64748b",
    stockColor: "#64748b",
    opacity: 0.55,
  },
  in_stock: {
    borderColor: "#dbe7ff",
    borderHoverColor: "#bfd4ff",
    bgColor: "#f8fbff",
    hoverBgColor: "#f2f8ff",
    accentColor: "#0d6efd",
    titleColor: "#1f2937",
    stockColor: "#0f172a",
  },
  high_stock: {
    borderColor: "#bcebd2",
    borderHoverColor: "#95dfbc",
    bgColor: "#f1fcf6",
    hoverBgColor: "#e9f9f1",
    accentColor: "#198754",
    titleColor: "#14532d",
    stockColor: "#198754",
  },
  low_stock: {
    borderColor: "#ffe1a8",
    borderHoverColor: "#ffd27a",
    bgColor: "#fffaf0",
    hoverBgColor: "#fff4e1",
    accentColor: "#f59e0b",
    titleColor: "#7c4a03",
    stockColor: "#b45309",
  },
  out_of_stock: {
    borderColor: "#ffc9d5",
    borderHoverColor: "#ff9fb5",
    bgColor: "#fff5f7",
    hoverBgColor: "#ffeef2",
    accentColor: "#d92550",
    titleColor: "#9f1239",
    stockColor: "#be123c",
  },
  no_cost: {
    borderColor: "#d5dbe3",
    borderHoverColor: "#bec8d4",
    bgColor: "#f7f9fc",
    hoverBgColor: "#f2f5f9",
    accentColor: "#5d6d7e",
    titleColor: "#374151",
    stockColor: "#4b5563",
  },
};

const DEFAULT_LOW_STOCK_THRESHOLD = 10;

const resolveStockStatusKey = (item) => {
  if (item?.is_active === false || item?.is_active === "false") {
    return "inactive";
  }
  const count = Number(item?.count || 0);
  if (count <= 0 || item?.status_key === "out_of_stock") {
    return "out_of_stock";
  }

  const costPrice = Math.max(
    Number(item?.price_new || 0),
    Number(item?.avarage_price || 0),
  );
  if (costPrice <= 0 || item?.status_key === "no_cost") {
    return "no_cost";
  }

  const minThreshold = Number(item?.min_stock_threshold || 0);
  const isLowStock =
    minThreshold > 0
      ? count <= minThreshold
      : count <= DEFAULT_LOW_STOCK_THRESHOLD;

  if (isLowStock || item?.status_key === "low_stock") return "low_stock";
  return item?.status_key || "in_stock";
};

const resolveStockCardVisualKey = (item, statusKey) => {
  if (item?.is_active === false || item?.is_active === "false") {
    return "inactive";
  }
  const effectiveStatusKey = statusKey || resolveStockStatusKey(item);
  if (effectiveStatusKey === "inactive") return "inactive";
  if (effectiveStatusKey === "out_of_stock") return "out_of_stock";
  if (effectiveStatusKey === "low_stock") return "low_stock";
  if (effectiveStatusKey === "no_cost") return "no_cost";

  const count = Number(item?.count || 0);
  const minThreshold = Number(item?.min_stock_threshold || 0);
  const maxThreshold = Number(item?.max_stock_threshold || 0);
  const highStockBaseline =
    maxThreshold > 0 ? maxThreshold : minThreshold > 0 ? minThreshold * 5 : 30;

  if (count >= highStockBaseline) return "high_stock";
  return "in_stock";
};

const STOCK_MAIN_COLUMNS_STORAGE_KEY = "stock_main_columns";
const STOCK_TABLE_EXTRA_COLUMNS_STORAGE_KEY = "stock_table_extra_columns_v2";
const STOCK_LATEST_LOG_COLUMN_MIGRATION_KEY =
  "stock_latest_log_column_migrated";
const STOCK_MIN_STOCK_COLUMN_MIGRATION_KEY =
  "stock_min_stock_detail_column_migrated_v1";
const STOCK_DETAIL_ACTIVE_THRESHOLDS_ORDER_MIGRATION_KEY =
  "stock_detail_active_thresholds_order_migrated_v1";
const STOCK_MAIN_COLUMN_DEFS = [
  {
    key: "sku_barcode",
    label: "SKU / Barcode",
    width: 120,
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "count",
    label: "Tồn kho",
    width: 120,
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "price_new",
    label: "Giá mua",
    width: 120,
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "min_stock_threshold",
    label: "Tồn tối thiểu",
    width: 100,
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "total",
    label: "Tổng nhập",
    width: 110,
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "total_value",
    label: "Giá trị tồn",
    width: 130,
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "status_key",
    label: "Trạng thái",
    width: 100,
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
];
const STOCK_DETAIL_DEFAULT_COLUMN_KEYS = [
  /* LEGACY_STOCK_DEFAULT_ID_COLUMN_START
  "id",
  LEGACY_STOCK_DEFAULT_ID_COLUMN_END */
  "is_active",
  "min_stock_threshold",
  "max_stock_threshold",
  "category_name",
  "brand_name",
  "packing_specification",
  "warehouse_name",
  "batch_code",
  "storage_location",
  "stock_note",
  "provider_name",
  "income",
  "latest_purchase_unit_price",
  "latest_purchase_price",
  "avarage_price",
  "nearest_expiry_date",
  "is_alert_expiry_7_days",
  "is_alert_expiry_3_days",
  "created_at",
  "updated_at",
];
const STOCK_EXTRA_COLUMN_DEFS = [
  /* LEGACY_STOCK_ID_DETAIL_COLUMN_START
  {
    key: "id",
    label: "ID",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  LEGACY_STOCK_ID_DETAIL_COLUMN_END */
  {
    key: "is_active",
    label: "Trạng thái sử dụng",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "min_stock_threshold",
    label: "Tồn tối thiểu",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "max_stock_threshold",
    label: "Tồn tối đa",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "category_name",
    label: "Danh mục",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "brand_name",
    label: "Thương hiệu",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "packing_specification",
    label: "Quy cách đóng gói",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "warehouse_name",
    label: "Kho",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "batch_code",
    label: "Mã lô",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "storage_location",
    label: "Vị trí lưu kho",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "stock_note",
    label: "Ghi chú",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "avarage_price",
    label: "Giá mua trung bình",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "provider_name",
    label: "Nhà cung cấp",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "income",
    label: "Nhập gần nhất",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "latest_purchase_unit_price",
    label: "Giá mua gần nhất",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "latest_purchase_price",
    label: "Giá trị nhập gần nhất",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "nearest_expiry_date",
    label: "HSD gần nhất",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  /* LEGACY_STOCK_DETAIL_HIDDEN_INTERNAL_COLUMNS_START
  {
    key: "user_id",
    label: "User ID",
    tableHeaderClass: "text-right",
    tableCellClass: "text-right",
    cardValueClass: "text-right",
  },
  {
    key: "isSeen",
    label: "Đã xem",
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "is_alert_down_20",
    label: "Cảnh báo giảm 20%",
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "is_alert_down_10",
    label: "Cảnh báo giảm 10%",
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  {
    key: "softDeleted",
    label: "Đã xóa mềm",
    tableHeaderClass: "text-center",
    tableCellClass: "text-center",
    cardValueClass: "text-center",
  },
  LEGACY_STOCK_DETAIL_HIDDEN_INTERNAL_COLUMNS_END */
  {
    key: "is_alert_expiry_7_days",
    label: "Cảnh báo HSD 7 ngày",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "is_alert_expiry_3_days",
    label: "Cảnh báo HSD 3 ngày",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "created_at",
    label: "Ngày tạo",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
  {
    key: "updated_at",
    label: "Cập nhật gần nhất",
    tableHeaderClass: "text-start",
    tableCellClass: "text-start",
    cardValueClass: "text-left",
  },
];
const STOCK_EXPANDED_FIXED_COLUMN_DEFS = [
  /* LEGACY_STOCK_EXPANDED_FIXED_COLUMNS_START
  {
    key: "category_name",
    label: "Danh mục",
    tableCellClass: "text-left",
    cardValueClass: "text-left",
  },
  {
    key: "warehouse_name",
    label: "Kho",
    tableCellClass: "text-left",
    cardValueClass: "text-left",
  },
  LEGACY_STOCK_EXPANDED_FIXED_COLUMNS_END */
];

const getProviderNameFromStockItem = (item) => {
  const providerName = item?.company_provider_item?.company_provider?.name;
  const providerItemName = item?.company_provider_item?.name;
  return providerName || providerItemName || "-";
};

const resolveLatestImportQuantity = (item) => {
  const latestLogQuantity =
    item?.latest_stock_log_count ?? item?.latest_stock_log?.count;
  if (
    latestLogQuantity !== null &&
    latestLogQuantity !== undefined &&
    latestLogQuantity !== ""
  ) {
    return latestLogQuantity;
  }

  /* LEGACY_STOCK_LATEST_IMPORT_QUANTITY_FROM_INCOME_START
  return item?.income || 0;
  LEGACY_STOCK_LATEST_IMPORT_QUANTITY_FROM_INCOME_END */
  return null;
};

const resolveLatestImportUnitPrice = (item) => {
  const latestLogUnitPrice =
    item?.latest_stock_log_unit_price ?? item?.latest_stock_log?.unitPrice;
  if (
    latestLogUnitPrice !== null &&
    latestLogUnitPrice !== undefined &&
    latestLogUnitPrice !== ""
  ) {
    return latestLogUnitPrice;
  }

  /* LEGACY_STOCK_LATEST_IMPORT_UNIT_PRICE_FROM_PRICE_NEW_START
  return item?.price_new || 0;
  LEGACY_STOCK_LATEST_IMPORT_UNIT_PRICE_FROM_PRICE_NEW_END */
  return null;
};

const resolveLatestImportValue = (item) => {
  /* LEGACY_LATEST_PURCHASE_PRICE_VALUE_START
  const providerPrice = Number(item?.company_provider_item?.price_income || 0);
  if (providerPrice > 0) return providerPrice;
  const importPrice = Number(item?.price_new || 0);
  if (importPrice > 0) return importPrice;
  return Number(item?.avarage_price || 0);
  LEGACY_LATEST_PURCHASE_PRICE_VALUE_END */
  /* LEGACY_STOCK_LATEST_IMPORT_VALUE_FROM_STOCK_FIELDS_START
  const latestImportQuantity = Number(item?.income || 0);
  const latestImportUnitPrice = Number(item?.price_new || 0);
  return latestImportQuantity * latestImportUnitPrice;
  LEGACY_STOCK_LATEST_IMPORT_VALUE_FROM_STOCK_FIELDS_END */
  const latestImportQuantity = parseStockNumericValue(
    resolveLatestImportQuantity(item),
  );
  const latestImportUnitPrice = parseStockNumericValue(
    resolveLatestImportUnitPrice(item),
  );

  if (latestImportQuantity === null || latestImportUnitPrice === null) {
    return null;
  }

  return latestImportQuantity * latestImportUnitPrice;
};

const formatDateValue = (value, includeTime = false) => {
  if (!value) return "-";

  if (
    !includeTime &&
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }

  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return "-";

  if (includeTime) {
    return dateValue.toLocaleString("vi-VN", {
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return dateValue.toLocaleDateString("vi-VN");
};

const formatBooleanValue = (value) => {
  if (value === null || value === undefined) return "-";
  return value ? "Có" : "Không";
};

const resolveStockExtraColumnValue = (item, key) => {
  switch (key) {
    case "id":
      return item?.id || "-";
    case "category_name":
      return item?.category_name || "-";
    case "brand_name":
      return item?.brand_name || "-";
    case "packing_specification":
      return item?.packing_specification || "-";
    case "min_stock_threshold":
      return formatStockQuantity(item?.min_stock_threshold || 0, item?.unit);
    case "max_stock_threshold":
      return Number(item?.max_stock_threshold || 0) > 0
        ? formatStockQuantity(item?.max_stock_threshold, item?.unit)
        : "Không giới hạn";
    case "is_active":
      return item?.is_active === false || item?.is_active === "false" ? (
        <span className="badge badge-light text-muted">Ngừng sử dụng</span>
      ) : (
        <span className="badge badge-success">Đang sử dụng</span>
      );
    case "warehouse_name":
      return item?.warehouse_name || "-";
    case "batch_code":
      return item?.batch_code || "-";
    case "storage_location":
      return item?.storage_location || "-";
    case "stock_note":
      return item?.stock_note || "-";
    case "avarage_price":
      return formatCurrency(item?.avarage_price || 0);
    case "income": {
      const latestImportQuantity = resolveLatestImportQuantity(item);
      /* LEGACY_STOCK_INCOME_COLUMN_FROM_STOCK_INCOME_START
      return formatStockQuantity(item?.income || 0, item?.unit);
      LEGACY_STOCK_INCOME_COLUMN_FROM_STOCK_INCOME_END */
      return latestImportQuantity === null
        ? "-"
        : formatStockQuantity(latestImportQuantity, item?.unit);
    }
    case "provider_name":
      return getProviderNameFromStockItem(item);
    case "latest_purchase_unit_price": {
      const latestImportUnitPrice = resolveLatestImportUnitPrice(item);
      return latestImportUnitPrice === null
        ? "-"
        : formatCurrency(latestImportUnitPrice);
    }
    case "latest_purchase_price": {
      const latestImportValue = resolveLatestImportValue(item);
      /* LEGACY_STOCK_LATEST_IMPORT_VALUE_DECIMAL_CURRENCY_START
      return formatCurrency(resolveLatestImportValue(item) || 0);
      LEGACY_STOCK_LATEST_IMPORT_VALUE_DECIMAL_CURRENCY_END */
      return latestImportValue === null
        ? "-"
        : formatRoundedCurrency(latestImportValue);
    }
    case "nearest_expiry_date":
      return formatDateValue(item?.nearest_expiry_date);
    case "user_id":
      return item?.user_id || "-";
    case "isSeen":
      return formatBooleanValue(item?.isSeen);
    case "is_alert_down_20":
      return formatBooleanValue(item?.is_alert_down_20);
    case "is_alert_down_10":
      return formatBooleanValue(item?.is_alert_down_10);
    case "softDeleted":
      return formatBooleanValue(item?.softDeleted);
    case "is_alert_expiry_7_days":
      return formatBooleanValue(item?.is_alert_expiry_7_days);
    case "is_alert_expiry_3_days":
      return formatBooleanValue(item?.is_alert_expiry_3_days);
    case "created_at":
      return formatDateValue(item?.createdAt, true);
    case "updated_at":
      return formatDateValue(item?.updatedAt, true);
    default:
      return "-";
  }
};

const resolveExtraColumnWidth = (key) => {
  switch (key) {
    case "id":
      return 90;
    case "category_name":
    case "brand_name":
    case "packing_specification":
    case "warehouse_name":
    case "batch_code":
    case "storage_location":
      return 150;
    case "min_stock_threshold":
    case "max_stock_threshold":
      return 120;
    case "is_active":
      return 130;
    case "stock_note":
      return 220;
    case "avarage_price":
    case "latest_purchase_unit_price":
    case "latest_purchase_price":
      return 150;
    case "income":
      return 130;
    case "provider_name":
      return 180;
    case "nearest_expiry_date":
      return 140;
    case "user_id":
      return 100;
    case "isSeen":
    case "is_alert_down_20":
    case "is_alert_down_10":
    case "softDeleted":
    case "is_alert_expiry_7_days":
    case "is_alert_expiry_3_days":
      return 150;
    case "created_at":
    case "updated_at":
      return 170;
    default:
      return 150;
  }
};

const resolveMainColumnWidth = (key) => {
  const column = STOCK_MAIN_COLUMN_DEFS.find((item) => item.key === key);
  return column?.width || 120;
};

const renderStockMainColumnValue = (
  item,
  key,
  {
    statusKey = "in_stock",
    inventoryValue = 0,
    isCard = false,
    cardVisualKey = "",
    cardVisualStyle = {},
  } = {},
) => {
  switch (key) {
    case "sku_barcode": {
      const skuDisplayValue = resolveOptionalDisplayText(item?.sku);
      const barcodeDisplayValue = resolveOptionalDisplayText(item?.barcode);
      return (
        <>
          <div style={{ whiteSpace: "nowrap" }}>{skuDisplayValue || "-"}</div>
          <small className="text-muted" style={{ whiteSpace: "nowrap" }}>
            {barcodeDisplayValue || "-"}
          </small>
        </>
      );
    }
    case "count":
      return (
        <>
          <div
            className={isCard ? "font-weight-semibold" : ""}
            style={isCard ? { color: cardVisualStyle.stockColor } : undefined}
          >
            {formatStockQuantity(item?.count || 0, item?.unit)}
          </div>
          {isCard && cardVisualKey === "high_stock" && (
            <small
              className="d-block font-weight-semibold"
              style={{ color: cardVisualStyle.accentColor }}
            >
              Tồn nhiều
            </small>
          )}
          {isCard && cardVisualKey === "low_stock" && (
            <small
              className="d-block font-weight-semibold"
              style={{ color: cardVisualStyle.accentColor }}
            >
              Sắp hết
            </small>
          )}
        </>
      );
    case "min_stock_threshold":
      return formatStockNumber(item?.min_stock_threshold || 0);
    case "price_new":
      return formatCurrency(item?.price_new || 0);
    case "total":
      return formatStockQuantity(item?.total || 0, item?.unit);
    case "total_value":
      /* LEGACY_STOCK_TOTAL_VALUE_DECIMAL_CURRENCY_START
      return formatCurrency(inventoryValue);
      LEGACY_STOCK_TOTAL_VALUE_DECIMAL_CURRENCY_END */
      return formatRoundedCurrency(inventoryValue);
    case "status_key": {
      const isInactive =
        statusKey === "inactive" ||
        item?.is_active === false ||
        item?.is_active === "false";
      return (
        <Badge
          color={isInactive ? "light" : STATUS_COLOR[statusKey] || "secondary"}
          className={isInactive ? "text-muted border" : ""}
          pill
        >
          <IntlMessages
            id={
              isInactive
                ? "stock.dashboard.status.inactive"
                : STATUS_LABEL[statusKey] || STATUS_LABEL.in_stock
            }
          />
        </Badge>
      );
    }
    default:
      return "-";
  }
};

const resolveOptionalDisplayText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const resolveProductNameWithUnit = (item) => {
  const productName = resolveOptionalDisplayText(item?.name);
  const unitName = resolveOptionalDisplayText(item?.unit);
  if (!productName) return unitName ? `(${unitName})` : "";
  return unitName ? `${productName} (${unitName})` : productName;
};

const resolveInitialVisibleColumnKeys = (
  storageKey,
  columnDefs,
  defaultKeys,
) => {
  const availableColumnKeys = new Set(columnDefs.map((item) => item.key));
  const normalizedDefaultKeys = defaultKeys.filter((key) =>
    availableColumnKeys.has(key),
  );
  const fallbackColumnKeys =
    normalizedDefaultKeys.length > 0
      ? normalizedDefaultKeys
      : columnDefs.map((item) => item.key);
  if (typeof window === "undefined") return fallbackColumnKeys;

  try {
    const storedValue = window.localStorage.getItem(storageKey);
    if (!storedValue) return fallbackColumnKeys;

    const parsed = JSON.parse(storedValue);
    if (!Array.isArray(parsed)) return fallbackColumnKeys;

    const normalized = parsed.filter((key) => availableColumnKeys.has(key));
    return normalized.length > 0 ? normalized : fallbackColumnKeys;
  } catch (error) {
    return fallbackColumnKeys;
  }
};

const resolveInitialVisibleMainColumns = () =>
  resolveInitialVisibleColumnKeys(
    STOCK_MAIN_COLUMNS_STORAGE_KEY,
    STOCK_MAIN_COLUMN_DEFS,
    STOCK_MAIN_COLUMN_DEFS.map((item) => item.key),
  );

const resolveInitialVisibleExtraColumns = () =>
  resolveInitialVisibleColumnKeys(
    STOCK_TABLE_EXTRA_COLUMNS_STORAGE_KEY,
    STOCK_EXTRA_COLUMN_DEFS,
    STOCK_DETAIL_DEFAULT_COLUMN_KEYS,
  );

const STOCK_NUMBER_FORMATTER = new Intl.NumberFormat("vi-VN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const parseStockNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const rawValue = String(value).trim();
  if (!rawValue) return null;

  const lastCommaIndex = rawValue.lastIndexOf(",");
  const lastDotIndex = rawValue.lastIndexOf(".");
  let normalizedValue = rawValue.replace(/\s/g, "");

  if (lastCommaIndex > -1 && lastDotIndex > -1) {
    const decimalSeparator = lastCommaIndex > lastDotIndex ? "," : ".";
    const thousandSeparator = decimalSeparator === "," ? "." : ",";
    normalizedValue = normalizedValue
      .replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
      .replace(decimalSeparator, ".");
  } else if (lastCommaIndex > -1) {
    normalizedValue = normalizedValue.replace(/\./g, "").replace(",", ".");
  } else {
    normalizedValue = normalizedValue.replace(/,/g, "");
  }

  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const compactStockNumber = (value, maximumFractionDigits = 2) => {
  const numericValue = parseStockNumericValue(value);
  if (numericValue === null) return null;

  const factor = 10 ** maximumFractionDigits;
  return Math.trunc(numericValue * factor) / factor;
};

const formatStockNumber = (value, fallback = "0") => {
  const compactValue = compactStockNumber(value);
  if (compactValue === null) return fallback;
  return STOCK_NUMBER_FORMATTER.format(compactValue);
};

const formatStockQuantity = (value, unit = "") => {
  const unitText = unit ? ` ${unit}` : "";
  return `${formatStockNumber(value)}${unitText}`;
};

const formatCurrency = (value) => `${formatStockNumber(value)} đ`;

const formatRoundedCurrency = (value) => {
  const numericValue = parseStockNumericValue(value);
  if (numericValue === null) return "0 đ";
  return `${STOCK_NUMBER_FORMATTER.format(Math.round(numericValue))} đ`;
};

const STOCK_PRODUCT_VIEW_MODE_STORAGE_KEY = "stock_product_view_mode";
const STOCK_TABLE_VIEW_TEMPORARILY_DISABLED = true;
const STOCK_CARD_SELECTOR_COLUMN_WIDTH = 44;
const STOCK_CARD_ACTION_COLUMN_WIDTH = 70;
const STOCK_CARD_LEFT_ACCENT_BORDER_WIDTH = 4;
const STOCK_CARD_RIGHT_BORDER_WIDTH = 1;
const STOCK_CARD_BODY_HORIZONTAL_PADDING = 16;
const STOCK_CARD_HEADER_PADDING_LEFT = STOCK_CARD_BODY_HORIZONTAL_PADDING;
const STOCK_CARD_HEADER_PADDING_RIGHT =
  STOCK_CARD_BODY_HORIZONTAL_PADDING + STOCK_CARD_RIGHT_BORDER_WIDTH;

const resolveStockCardMainColumnGridWidth = (key) => {
  switch (key) {
    case "sku_barcode":
      return "max-content";
    case "count":
      return "minmax(0, 0.82fr)";
    case "price_new":
      return "minmax(0, 0.9fr)";
    case "min_stock_threshold":
      return "minmax(0, 0.76fr)";
    case "total":
      return "minmax(0, 0.82fr)";
    case "total_value":
      return "minmax(0, 0.92fr)";
    case "status_key":
      return "minmax(0, 0.86fr)";
    default:
      return "minmax(0, 0.8fr)";
  }
};

const resolveStockCardGridTemplate = (visibleMainColumns = []) => {
  const dynamicColumns = visibleMainColumns
    .map((column) => resolveStockCardMainColumnGridWidth(column.key))
    .join(" ");

  return [
    `${STOCK_CARD_SELECTOR_COLUMN_WIDTH}px`,
    "minmax(0, 1.35fr)",
    dynamicColumns,
    `${STOCK_CARD_ACTION_COLUMN_WIDTH}px`,
  ]
    .filter(Boolean)
    .join(" ");
};

const resolveInitialProductViewMode = () => {
  /* LEGACY_PRODUCT_VIEW_MODE_DEFAULT_START
  if (typeof window === "undefined") return "table";
  const storedMode = window.localStorage.getItem(
    STOCK_PRODUCT_VIEW_MODE_STORAGE_KEY,
  );
  return storedMode === "card" ? "card" : "table";
  LEGACY_PRODUCT_VIEW_MODE_DEFAULT_END */
  if (typeof window === "undefined") return "card";
  /* LEGACY_STOCK_PRODUCT_VIEW_MODE_FROM_STORAGE_START
  const storedMode = window.localStorage.getItem(
    STOCK_PRODUCT_VIEW_MODE_STORAGE_KEY,
  );
  return storedMode === "table" ? "table" : "card";
  LEGACY_STOCK_PRODUCT_VIEW_MODE_FROM_STORAGE_END */
  return "card";
};

const StockDashboardLayout = ({
  uiMode,
  onToggleUiMode,
  filters,
  filterOptions,
  onFilterChange,
  onApplyFilters,
  onResetFilters,
  onSearchKeyDown,
  onSearchChange,
  onSearch,
  data,
  allData,
  showTemplate,
  page,
  selectedItems,
  onToggleSelectAllCurrent,
  onToggleItem,
  onChangePage,
  onChangePageSize,
  onExport,
  onOpenAdd,
  onOpenImport,
  onOpenUpdate,
  onOpenUpdateCount,
  onOpenImportHistory,
  onDeleteItem,
  onDeleteSelected,
  dashboardData,
  isLoadingDashboard,
  canCreate,
  canUpdate,
  canDelete,
}) => {
  const summary = dashboardData?.summary || {};
  const breakdown = dashboardData?.breakdown || {};
  const alerts = dashboardData?.alerts || { counts: {}, items: {} };
  const recentImports = dashboardData?.recent_imports || [];
  /* LEGACY_STOCK_PRODUCT_VIEW_MODE_WITH_SETTER_START
  const [productViewMode, setProductViewMode] = React.useState(
    resolveInitialProductViewMode,
  );
  LEGACY_STOCK_PRODUCT_VIEW_MODE_WITH_SETTER_END */
  const [productViewMode] = React.useState(resolveInitialProductViewMode);
  /* LEGACY_STOCK_INLINE_CARD_HOVER_STATE_START
  const [hoveredCardId, setHoveredCardId] = React.useState(null);
  LEGACY_STOCK_INLINE_CARD_HOVER_STATE_END */
  const [visibleMainColumnKeys, setVisibleMainColumnKeys] = React.useState(
    resolveInitialVisibleMainColumns,
  );
  const [visibleExtraColumnKeys, setVisibleExtraColumnKeys] = React.useState(
    resolveInitialVisibleExtraColumns,
  );
  const [expandedTableRowIds, setExpandedTableRowIds] = React.useState([]);
  const [expandedCardRowIds, setExpandedCardRowIds] = React.useState([]);
  const [openMenuStockId, setOpenMenuStockId] = React.useState(null);
  const [overviewMode, setOverviewMode] = React.useState("item");
  const handleToolbarActionChange = (event) => {
    const nextPath = event.target.value;
    if (!nextPath) return;
    window.location.assign(nextPath);
  };

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (
      window.localStorage.getItem(STOCK_LATEST_LOG_COLUMN_MIGRATION_KEY) === "1"
    ) {
      return;
    }

    setVisibleExtraColumnKeys((previous) => {
      if (
        !previous.includes("income") ||
        previous.includes("latest_purchase_unit_price")
      ) {
        return previous;
      }

      const next = [...previous];
      next.splice(
        previous.indexOf("income") + 1,
        0,
        "latest_purchase_unit_price",
      );
      return next;
    });
    window.localStorage.setItem(STOCK_LATEST_LOG_COLUMN_MIGRATION_KEY, "1");
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (
      window.localStorage.getItem(STOCK_MIN_STOCK_COLUMN_MIGRATION_KEY) === "1"
    ) {
      return;
    }

    setVisibleExtraColumnKeys((previous) => {
      if (previous.includes("min_stock_threshold")) {
        return previous;
      }

      const next = [...previous];
      const maxIndex = previous.indexOf("max_stock_threshold");
      if (maxIndex !== -1) {
        next.splice(maxIndex, 0, "min_stock_threshold");
      } else {
        next.push("min_stock_threshold");
      }
      return next;
    });
    window.localStorage.setItem(STOCK_MIN_STOCK_COLUMN_MIGRATION_KEY, "1");
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (
      window.localStorage.getItem(
        STOCK_DETAIL_ACTIVE_THRESHOLDS_ORDER_MIGRATION_KEY,
      ) === "1"
    ) {
      return;
    }

    setVisibleExtraColumnKeys((previous) => {
      const filtered = previous.filter(
        (key) =>
          key !== "is_active" &&
          key !== "min_stock_threshold" &&
          key !== "max_stock_threshold",
      );
      return [
        "is_active",
        "min_stock_threshold",
        "max_stock_threshold",
        ...filtered,
      ];
    });
    window.localStorage.setItem(
      STOCK_DETAIL_ACTIVE_THRESHOLDS_ORDER_MIGRATION_KEY,
      "1",
    );
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      STOCK_PRODUCT_VIEW_MODE_STORAGE_KEY,
      productViewMode,
    );
  }, [productViewMode]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      STOCK_MAIN_COLUMNS_STORAGE_KEY,
      JSON.stringify(visibleMainColumnKeys),
    );
  }, [visibleMainColumnKeys]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      STOCK_TABLE_EXTRA_COLUMNS_STORAGE_KEY,
      JSON.stringify(visibleExtraColumnKeys),
    );
  }, [visibleExtraColumnKeys]);

  React.useEffect(() => {
    setExpandedTableRowIds([]);
    setExpandedCardRowIds([]);
  }, [data, page.currentPage]);

  /* LEGACY_OVERVIEW_DONUT_DATA_START
  const doughnutData = {
    labels: [
      "Còn hàng",
      "Sắp hết",
      "Hết hàng",
      "Không nhập giá vốn",
    ],
    datasets: [
      {
        borderWidth: 2,
        borderColor: ["#ffffff", "#ffffff", "#ffffff", "#ffffff"],
        data: [
          breakdown?.in_stock?.count || 0,
          breakdown?.low_stock?.count || 0,
          breakdown?.out_of_stock?.count || 0,
          breakdown?.no_cost?.count || 0,
        ],
        backgroundColor: ["#3ac47d", "#f7b924", "#d92550", "#5d6d7e"],
        hoverBackgroundColor: ["#36b673", "#e6aa20", "#c12048", "#4f5e6b"],
      },
    ],
  };
  LEGACY_OVERVIEW_DONUT_DATA_END */

  const hasRows = Array.isArray(data) && data.length > 0;
  const allSelectedOnPage =
    hasRows && data.every((item) => selectedItems.includes(item.id));
  const visibleMainColumns = STOCK_MAIN_COLUMN_DEFS.filter((column) =>
    visibleMainColumnKeys.includes(column.key),
  );
  const visibleExtraColumns = STOCK_EXTRA_COLUMN_DEFS.filter((column) =>
    visibleExtraColumnKeys.includes(column.key),
  );
  const stockCardGridTemplate =
    resolveStockCardGridTemplate(visibleMainColumns);
  const expandedDetailColumns = [
    ...STOCK_EXPANDED_FIXED_COLUMN_DEFS,
    ...visibleExtraColumns,
  ];
  const tableVisibleColumnCount =
    4 + visibleMainColumns.length + visibleExtraColumns.length;
  const toggleMainColumnVisibility = (columnKey) => {
    setVisibleMainColumnKeys((previous) => {
      const next = previous.includes(columnKey)
        ? previous.filter((item) => item !== columnKey)
        : [...previous, columnKey];
      const orderedKeys = STOCK_MAIN_COLUMN_DEFS.map((column) => column.key);
      return orderedKeys.filter((item) => next.includes(item));
    });
  };
  const toggleExtraColumnVisibility = (columnKey) => {
    setVisibleExtraColumnKeys((previous) => {
      const next = previous.includes(columnKey)
        ? previous.filter((item) => item !== columnKey)
        : [...previous, columnKey];
      const orderedKeys = STOCK_EXTRA_COLUMN_DEFS.map((column) => column.key);
      return orderedKeys.filter((item) => next.includes(item));
    });
  };
  const toggleTableRowDetail = (rowId) => {
    setExpandedTableRowIds((previous) =>
      previous.includes(rowId)
        ? previous.filter((item) => item !== rowId)
        : [...previous, rowId],
    );
  };
  const toggleCardRowDetail = (rowId) => {
    setExpandedCardRowIds((previous) =>
      previous.includes(rowId)
        ? previous.filter((item) => item !== rowId)
        : [...previous, rowId],
    );
  };
  const overviewItems = [
    {
      key: "in_stock",
      label: "Còn hàng",
      color: "#3ac47d",
    },
    {
      key: "low_stock",
      label: "Sắp hết hàng",
      color: "#f7b924",
    },
    {
      key: "out_of_stock",
      label: "Hết hàng",
      color: "#d92550",
    },
    {
      key: "no_cost",
      label: "Không nhập giá vốn",
      color: "#5d6d7e",
    },
  ];
  const resolveOverviewItemCount = (key) => {
    return Number(
      breakdown?.[key]?.item_count !== undefined
        ? breakdown[key].item_count
        : breakdown?.[key]?.count || 0,
    );
  };

  const resolveOverviewQuantity = (key) => {
    return Number(
      breakdown?.[key]?.quantity !== undefined
        ? breakdown[key].quantity
        : key === "out_of_stock"
          ? 0
          : breakdown?.[key]?.count || 0,
    );
  };

  const resolveOverviewPercent = (key) => {
    if (overviewMode === "item") {
      const rawPercent = Number(breakdown?.[key]?.percent);
      if (Number.isFinite(rawPercent)) {
        return rawPercent;
      }
      const total = Number(
        breakdown?.total_items || breakdown?.total || summary.total_items || 0,
      );
      if (!total) return 0;
      const count = resolveOverviewItemCount(key);
      return (count * 100) / total;
    }

    const rawQuantityPercent = Number(breakdown?.[key]?.quantity_percent);
    if (Number.isFinite(rawQuantityPercent)) {
      return rawQuantityPercent;
    }
    const total = Number(
      breakdown?.total_quantity || summary.total_stock || 0,
    );
    if (!total) return 0;
    const qty = resolveOverviewQuantity(key);
    return (qty * 100) / total;
  };

  const overviewRows = overviewItems.map((overview) => {
    const itemCount = resolveOverviewItemCount(overview.key);
    const quantity = resolveOverviewQuantity(overview.key);
    const percent = resolveOverviewPercent(overview.key);
    const displayCount = overviewMode === "item" ? itemCount : quantity;

    return {
      ...overview,
      itemCount,
      quantity,
      count: displayCount,
      percent,
    };
  });
  const hasActiveStockSelectFilters = hasAnyActiveFilter(filters, [
    "category_name",
    "warehouse_name",
    "status",
  ]);
  const overviewTotalCount = overviewRows.reduce(
    (sum, row) => sum + row.count,
    0,
  );
  const overviewConicGradient = (() => {
    if (!overviewTotalCount) {
      return "conic-gradient(#e9ecef 0% 100%)";
    }

    let startPercent = 0;
    const segments = overviewRows
      .filter((row) => row.count > 0)
      .map((row) => {
        const segmentPercent = (row.count / overviewTotalCount) * 100;
        const endPercent = startPercent + segmentPercent;
        const colorRange = `${row.color} ${startPercent.toFixed(2)}% ${endPercent.toFixed(2)}%`;
        startPercent = endPercent;
        return colorRange;
      });

    if (!segments.length) {
      return "conic-gradient(#e9ecef 0% 100%)";
    }
    return `conic-gradient(${segments.join(", ")})`;
  })();
  const alertRows = [
    {
      key: "out_of_stock",
      count: Number(alerts?.counts?.out_of_stock || 0),
      text: "hết hàng",
      icon: "simple-icon-fire",
      color: "#d92550",
      bgColor: "rgba(217, 37, 80, 0.12)",
    },
    {
      key: "low_stock",
      count: Number(alerts?.counts?.low_stock || 0),
      text: "sắp hết hàng",
      icon: "simple-icon-exclamation",
      color: "#f7b924",
      bgColor: "rgba(247, 185, 36, 0.12)",
    },
    {
      key: "no_cost",
      count: Number(alerts?.counts?.no_cost || 0),
      text: "chưa nhập giá vốn",
      icon: "simple-icon-info",
      color: "#5d6d7e",
      bgColor: "rgba(93, 109, 126, 0.12)",
    },
  ];
  const quickActions = [
    {
      key: "warehouse_import",
      to: "/app/pages/warehouse-import",
      title: "Nhập kho",
      subtitle: "Thêm hàng vào kho",
      icon: "simple-icon-login",
      iconColor: "#0d6efd",
      iconBg: "rgba(13, 110, 253, 0.12)",
    },
    {
      key: "warehouse_export",
      to: "/app/pages/warehouse-export",
      title: "Xuất kho",
      subtitle: "Xuất hàng bán hoặc nội bộ",
      icon: "simple-icon-logout",
      iconColor: "#f7b924",
      iconBg: "rgba(247, 185, 36, 0.12)",
    },
    {
      key: "warehouse_transfer",
      to: "/app/pages/warehouse-transfer",
      title: "Chuyển kho",
      subtitle: "Chuyển hàng giữa các kho",
      icon: "simple-icon-refresh",
      iconColor: "#6f42c1",
      iconBg: "rgba(111, 66, 193, 0.12)",
    },
    {
      key: "inventory_audit",
      to: "/app/pages/inventory-audit",
      title: "Kiểm kê kho",
      subtitle: "Kiểm tra và đối chiếu tồn",
      icon: "simple-icon-check",
      iconColor: "#198754",
      iconBg: "rgba(25, 135, 84, 0.12)",
    },
  ];
  const kpiCards = [
    {
      key: "total_items",
      label: "Tổng mặt hàng",
      value: formatStockNumber(summary.total_items || 0),
      icon: "simple-icon-grid",
      iconColor: "#0d6efd",
      iconBg: "rgba(13, 110, 253, 0.12)",
    },
    {
      key: "total_stock",
      label: "Tổng tồn kho",
      value: formatStockNumber(summary.total_stock || 0),
      icon: "simple-icon-layers",
      iconColor: "#6f42c1",
      iconBg: "rgba(111, 66, 193, 0.12)",
    },
    {
      key: "total_stock_value",
      label: "Giá trị tồn kho",
      value: formatCurrency(summary.total_stock_value || 0),
      icon: "simple-icon-wallet",
      iconColor: "#198754",
      iconBg: "rgba(25, 135, 84, 0.12)",
    },
    {
      key: "stock_alert",
      label: "Sắp hết / Hết hàng",
      value: `${formatStockNumber(summary.low_stock_count || 0)} / ${formatStockNumber(summary.out_of_stock_count || 0)}`,
      icon: "simple-icon-bell",
      iconColor: "#dc3545",
      iconBg: "rgba(220, 53, 69, 0.12)",
    },
  ];

  const hasTemplateStocks =
    (Array.isArray(allData) && allData.some((s) => Boolean(s?.isTemplate))) ||
    (Array.isArray(data) && data.some((s) => Boolean(s?.isTemplate)));

  return (
    <div className="stock-dashboard-v2 disable-text-selection">
      <StockDashboardMobile
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        onApplyFilters={onApplyFilters}
        onResetFilters={onResetFilters}
        onSearchKeyDown={onSearchKeyDown}
        onSearchChange={onSearchChange}
        onSearch={onSearch}
        data={data}
        allData={allData}
        showTemplate={showTemplate}
        page={page}
        selectedItems={selectedItems}
        onToggleItem={onToggleItem}
        onChangePage={onChangePage}
        onChangePageSize={onChangePageSize}
        onExport={onExport}
        onOpenAdd={onOpenAdd}
        onOpenImport={onOpenImport}
        onOpenUpdate={onOpenUpdate}
        onOpenUpdateCount={onOpenUpdateCount}
        onDeleteItem={onDeleteItem}
        onDeleteSelected={onDeleteSelected}
        dashboardData={dashboardData}
        isLoadingDashboard={isLoadingDashboard}
        canCreate={canCreate}
        canUpdate={canUpdate}
        canDelete={canDelete}
      />
      <div className="d-none d-md-block">
        <Row>
          <Col xl="9">
            {hasTemplateStocks ? (
              <Row className="mb-3">
                <Col xs="12">
                  <TemplateWarningCard
                    onDismissed={() => {
                      if (onChangePage) onChangePage(1);
                    }}
                  />
                </Col>
              </Row>
            ) : null}
            {/* <Row className="mb-3">
            <Col
              xs="12"
              className="d-flex justify-content-between align-items-start flex-wrap"
            >
              <div>
                <h1 className="mb-1">
                  <IntlMessages id="menu.stock" />
                </h1>
                <small className="text-muted">
                  <IntlMessages id="stock.dashboard.subtitle" />
                </small>
              </div>
              <div className="d-flex align-items-center mt-2 mt-sm-0">
                <Badge
                  color={uiMode === "new" ? "primary" : "secondary"}
                  className="mr-2"
                >
                  {uiMode === "new" ? "New UI" : "Legacy UI"}
                </Badge>
                <Button
                  color={
                    uiMode === "new" ? "outline-secondary" : "outline-primary"
                  }
                  size="sm"
                  onClick={onToggleUiMode}
                >
                  {uiMode === "new" ? "Chuyển Legacy UI" : "Chuyển New UI"}
                </Button>
              </div>
            </Col>
          </Row> */}

            <Row className="mb-3">
              {kpiCards.map((card) => (
                <Col key={card.key} md="6" lg="3" className="mb-2">
                  <Card>
                    <CardBody className="py-3 d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center mr-2"
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          backgroundColor: card.iconBg,
                        }}
                      >
                        <i
                          className={card.icon}
                          style={{ color: card.iconColor, fontSize: "1.15rem" }}
                        />
                      </div>
                      <div>
                        <div className="text-muted text-small mb-2">
                          {card.label}
                        </div>
                        <div className="h6 mb-0">{card.value}</div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="mb-3">
              <CardBody>
                <div
                  className="mb-3"
                  style={{ overflowX: "auto", overflowY: "hidden" }}
                >
                  <div
                    className="d-flex align-items-center flex-nowrap justify-content-end mb-2"
                    style={{ minWidth: "max-content" }}
                  >
                    <UncontrolledDropdown className="d-inline-block mr-2 flex-shrink-0">
                      <DropdownToggle
                        caret
                        size="sm"
                        color="outline-dark"
                        className="text-nowrap"
                        style={{
                          ...STOCK_TOOLBAR_GHOST_BUTTON_STYLE,
                          ...STOCK_TOOLBAR_CONTROL_HEIGHT_STYLE,
                        }}
                      >
                        Cột hiển thị
                      </DropdownToggle>
                      {/* LEGACY_STOCK_COLUMN_DROPDOWN_MENU_START
                    <DropdownMenu right style={{ minWidth: 220 }}>
                    LEGACY_STOCK_COLUMN_DROPDOWN_MENU_END */}
                      <DropdownMenu
                        right
                        positionFixed
                        modifiers={{
                          preventOverflow: {
                            boundariesElement: "viewport",
                          },
                          computeStyle: {
                            gpuAcceleration: false,
                          },
                        }}
                        style={{ minWidth: 220, zIndex: 2300 }}
                      >
                        <DropdownItem header>Hiển thị chính</DropdownItem>
                        {STOCK_MAIN_COLUMN_DEFS.map((column) => (
                          <DropdownItem
                            key={`main-${column.key}`}
                            toggle={false}
                            className="px-3 py-2"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              toggleMainColumnVisibility(column.key);
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <Input
                                type="checkbox"
                                className="m-0 mr-2"
                                style={{ position: "static" }}
                                checked={visibleMainColumnKeys.includes(
                                  column.key,
                                )}
                                onChange={() =>
                                  toggleMainColumnVisibility(column.key)
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                              <span>{column.label}</span>
                            </div>
                          </DropdownItem>
                        ))}
                        <DropdownItem divider />
                        <DropdownItem header>Chi tiết mở rộng</DropdownItem>
                        {STOCK_EXTRA_COLUMN_DEFS.map((column) => (
                          <DropdownItem
                            key={`detail-${column.key}`}
                            toggle={false}
                            className="px-3 py-2"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              toggleExtraColumnVisibility(column.key);
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <Input
                                type="checkbox"
                                className="m-0 mr-2"
                                style={{ position: "static" }}
                                checked={visibleExtraColumnKeys.includes(
                                  column.key,
                                )}
                                onChange={() =>
                                  toggleExtraColumnVisibility(column.key)
                                }
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                              <span>{column.label}</span>
                            </div>
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>

                    <Input
                      bsSize="sm"
                      type="select"
                      className="mr-2 flex-shrink-0"
                      style={{
                        ...STOCK_TOOLBAR_SELECT_STYLE,
                        width: 170,
                        minWidth: 170,
                        maxWidth: 170,
                      }}
                      value={filters.category_name || ""}
                      onChange={(e) =>
                        onFilterChange("category_name", e.target.value)
                      }
                    >
                      <option value="">Danh mục: Tất cả</option>
                      {(filterOptions?.category_names || []).map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Input>

                    <Input
                      bsSize="sm"
                      type="select"
                      className="mr-2 flex-shrink-0"
                      style={{
                        ...STOCK_TOOLBAR_SELECT_STYLE,
                        width: 145,
                        minWidth: 145,
                        maxWidth: 145,
                      }}
                      value={filters.warehouse_name || ""}
                      onChange={(e) =>
                        onFilterChange("warehouse_name", e.target.value)
                      }
                    >
                      <option value="">Kho: Tất cả</option>
                      {(filterOptions?.warehouse_names || []).map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Input>

                    <Input
                      bsSize="sm"
                      type="select"
                      className="flex-shrink-0"
                      style={{
                        ...STOCK_TOOLBAR_SELECT_STYLE,
                        width: 175,
                        minWidth: 175,
                        maxWidth: 175,
                      }}
                      value={filters.status || ""}
                      onChange={(e) => onFilterChange("status", e.target.value)}
                    >
                      <option value="">Trạng thái: Tất cả</option>
                      <option value="in_stock">Còn hàng</option>
                      <option value="low_stock">Sắp hết</option>
                      <option value="out_of_stock">Hết hàng</option>
                      <option value="no_cost">Không nhập giá vốn</option>
                    </Input>

                    {/* LEGACY_STOCK_RESET_FILTER_ALWAYS_VISIBLE_START
                  <Button
                    size="sm"
                    color="outline-dark"
                    className="mr-2 flex-shrink-0"
                    onClick={onResetFilters}
                    style={{
                      ...STOCK_TOOLBAR_GHOST_BUTTON_STYLE,
                      ...STOCK_TOOLBAR_CONTROL_HEIGHT_STYLE,
                    }}
                  >
                    Bỏ lọc
                  </Button>
                  LEGACY_STOCK_RESET_FILTER_ALWAYS_VISIBLE_END */}
                    {hasActiveStockSelectFilters && (
                      <Button
                        size="sm"
                        color="outline-danger"
                        className="mr-2 flex-shrink-0"
                        onClick={onResetFilters}
                        style={{
                          ...STOCK_TOOLBAR_DANGER_BUTTON_STYLE,
                          ...STOCK_TOOLBAR_CONTROL_HEIGHT_STYLE,
                        }}
                      >
                        Bỏ lọc
                      </Button>
                    )}
                  </div>

                  {/* LEGACY_STOCK_ACTIONS_INLINE_WITH_FILTERS_START
                  <div className="d-flex align-items-center flex-nowrap ml-auto pl-2">
                  LEGACY_STOCK_ACTIONS_INLINE_WITH_FILTERS_END */}
                  <div
                    className="d-flex align-items-center flex-nowrap justify-content-end"
                    style={{ minWidth: "max-content" }}
                  >
                    {canCreate && (
                      <Button
                        size="sm"
                        color="link"
                        className="mr-2 flex-shrink-0"
                        onClick={onOpenImport}
                        style={STOCK_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                      >
                        <i className="simple-icon-cloud-upload mr-1" />
                        Nhập kho
                      </Button>
                    )}

                    <Button
                      size="sm"
                      color="link"
                      className="mr-2 flex-shrink-0"
                      onClick={onExport}
                      style={STOCK_TOOLBAR_SOFT_ACTION_BUTTON_STYLE}
                    >
                      <i className="simple-icon-cloud-download mr-1" />
                      Xuất Excel
                    </Button>

                    {/* LEGACY_STOCK_ACTION_SELECT_START
                    <Input
                      bsSize="sm"
                      type="select"
                      className="mr-2 flex-shrink-0"
                      style={{ width: 205, minWidth: 205, maxWidth: 205 }}
                      value=""
                      onChange={handleToolbarActionChange}
                    >
                      <option value="">Thao tác</option>
                      <option value="/app/pages/warehouse-import?action=create">
                        Tạo phiếu nhập kho
                      </option>
                      <option value="/app/pages/warehouse-export?action=create">
                        Tạo phiếu xuất kho
                      </option>
                      <option value="/app/pages/warehouse-transfer?action=create">
                        Tạo phiếu chuyển kho
                      </option>
                      <option value="/app/pages/inventory-audit?action=create">
                        Tạo phiếu kiểm kho
                      </option>
                    </Input>
                    LEGACY_STOCK_ACTION_SELECT_END */}
                    <UncontrolledDropdown className="d-inline-block mr-2 flex-shrink-0">
                      <DropdownToggle
                        caret
                        size="sm"
                        color="outline-dark"
                        className="text-nowrap"
                        style={STOCK_TOOLBAR_GHOST_BUTTON_STYLE}
                      >
                        Thao tác
                      </DropdownToggle>
                      <DropdownMenu
                        right
                        positionFixed
                        modifiers={{
                          preventOverflow: {
                            boundariesElement: "viewport",
                          },
                          computeStyle: {
                            gpuAcceleration: false,
                          },
                        }}
                        style={{ minWidth: 220, zIndex: 2400 }}
                      >
                        <DropdownItem
                          className="text-left text-start"
                          onClick={() =>
                            handleToolbarActionChange({
                              target: {
                                value:
                                  "/app/pages/warehouse-import?action=create",
                              },
                            })
                          }
                        >
                          <i className="simple-icon-login mr-2 text-primary" />
                          Tạo phiếu nhập kho
                        </DropdownItem>
                        <DropdownItem
                          className="text-left text-start"
                          onClick={() =>
                            handleToolbarActionChange({
                              target: {
                                value:
                                  "/app/pages/warehouse-export?action=create",
                              },
                            })
                          }
                        >
                          <i className="simple-icon-logout mr-2 text-warning" />
                          Tạo phiếu xuất kho
                        </DropdownItem>
                        <DropdownItem
                          className="text-left text-start"
                          onClick={() =>
                            handleToolbarActionChange({
                              target: {
                                value:
                                  "/app/pages/warehouse-transfer?action=create",
                              },
                            })
                          }
                        >
                          <i className="simple-icon-refresh mr-2 text-info" />
                          Tạo phiếu chuyển kho
                        </DropdownItem>
                        <DropdownItem
                          className="text-left text-start"
                          onClick={() =>
                            handleToolbarActionChange({
                              target: {
                                value:
                                  "/app/pages/inventory-audit?action=create",
                              },
                            })
                          }
                        >
                          <i className="simple-icon-check mr-2 text-success" />
                          Tạo phiếu kiểm kho
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>

                    {canCreate && (
                      <Button
                        size="sm"
                        color="primary"
                        className="flex-shrink-0"
                        onClick={onOpenAdd}
                      >
                        + Thêm mới
                      </Button>
                    )}

                    {selectedItems.length > 0 && canDelete && (
                      <Button
                        size="sm"
                        color="danger"
                        className="ml-2 flex-shrink-0"
                        onClick={onDeleteSelected}
                      >
                        Xóa ({selectedItems.length})
                      </Button>
                    )}
                  </div>
                </div>

                {!STOCK_TABLE_VIEW_TEMPORARILY_DISABLED &&
                productViewMode === "table" ? (
                  <div className="table-responsive">
                    <Table
                      bordered
                      hover
                      className="mb-2 text-center"
                      style={{ width: "100%" }}
                    >
                      <colgroup>
                        <col style={{ width: 40 }} />
                        <col style={{ width: "100%" }} />
                        {/* LEGACY_STOCK_TABLE_MAIN_COLGROUP_START
                    <col style={{ width: 170 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 130 }} />
                    LEGACY_STOCK_TABLE_MAIN_COLGROUP_END */}
                        {visibleMainColumns.map((column) => (
                          <col
                            key={`main-col-${column.key}`}
                            style={{
                              width: resolveMainColumnWidth(column.key),
                            }}
                          />
                        ))}
                        {visibleExtraColumns.map((column) => (
                          <col
                            key={`col-${column.key}`}
                            style={{
                              width: resolveExtraColumnWidth(column.key),
                            }}
                          />
                        ))}
                        <col style={{ width: 72 }} />
                        <col style={{ width: 46 }} />
                      </colgroup>
                      <thead className="thead-light">
                        <tr className="align-middle text-center">
                          <th
                            style={{ width: 40 }}
                            className="text-center align-middle p-0"
                          >
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ minHeight: 38 }}
                            >
                              <Input
                                type="checkbox"
                                className="m-0"
                                style={{ position: "static" }}
                                checked={allSelectedOnPage}
                                onChange={onToggleSelectAllCurrent}
                              />
                            </div>
                          </th>
                          <th>Sản phẩm</th>
                          {/* LEGACY_STOCK_TABLE_MAIN_HEADERS_START
                      <th className="text-start">SKU / Barcode</th>
                      <th>Tồn kho</th>
                      <th>Tồn tối thiểu</th>
                      <th>Giá mua</th>
                      <th>Tổng nhập</th>
                      <th>Giá trị tồn</th>
                      <th>Trạng thái</th>
                      LEGACY_STOCK_TABLE_MAIN_HEADERS_END */}
                          {visibleMainColumns.map((column) => (
                            <th
                              key={`main-header-${column.key}`}
                              className={column.tableHeaderClass || ""}
                            >
                              {column.label}
                            </th>
                          ))}
                          {/* LEGACY_STOCK_TABLE_HEADER_EXTRA_COLUMNS_START
                      <th>Nhà cung cấp</th>
                      <th>Giá nhập gần nhất</th>
                      <th>HSD gần nhất</th>
                      <th>Cập nhật gần nhất</th>
                      LEGACY_STOCK_TABLE_HEADER_EXTRA_COLUMNS_END */}
                          {visibleExtraColumns.map((column) => (
                            <th
                              key={`header-${column.key}`}
                              className={`d-none d-xl-table-cell ${column.tableHeaderClass || ""}`}
                            >
                              {column.label}
                            </th>
                          ))}
                          <th style={{ width: 72 }} className="text-center">
                            Khác
                          </th>
                          <th style={{ width: 46 }} className="text-center">
                            CT
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {!hasRows ? (
                          <tr className="align-middle text-center">
                            <td
                              colSpan={tableVisibleColumnCount}
                              className="text-center text-muted"
                            >
                              Chưa có dữ liệu...
                            </td>
                          </tr>
                        ) : (
                          data.map((item) => {
                            const statusKey = resolveStockStatusKey(item);
                            const productNameWithUnit =
                              resolveProductNameWithUnit(item);
                            const skuDisplayValue = resolveOptionalDisplayText(
                              item.sku,
                            );
                            const barcodeDisplayValue =
                              resolveOptionalDisplayText(item.barcode);
                            const isExpandedTableRow =
                              expandedTableRowIds.includes(item.id);
                            const inventoryValue =
                              Number(item.count || 0) *
                                Number(
                                  item.avarage_price || item.price_new || 0,
                                ) || 0;
                            return (
                              <React.Fragment key={item.id}>
                                {/* LEGACY_STOCK_TABLE_ROW_SIMPLE_START
                            <tr key={item.id} className="align-middle">...</tr>
                            LEGACY_STOCK_TABLE_ROW_SIMPLE_END */}
                                <tr
                                  className="align-middle"
                                  style={
                                    openMenuStockId === item.id
                                      ? {
                                          position: "relative",
                                          zIndex: 1050,
                                          opacity: 1,
                                        }
                                      : item?.is_active === false ||
                                        item?.is_active === "false"
                                        ? {
                                            opacity: 0.55,
                                            backgroundColor: "#f8fafc",
                                          }
                                        : undefined
                                  }
                                >
                                  <td className="text-center align-middle p-0">
                                    <div
                                      className="d-flex align-items-center justify-content-center"
                                      style={{ minHeight: 44 }}
                                    >
                                      <Input
                                        type="checkbox"
                                        className="m-0"
                                        style={{ position: "static" }}
                                        checked={selectedItems.includes(
                                          item.id,
                                        )}
                                        onChange={(e) =>
                                          onToggleItem(item.id, e)
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td
                                    className="py-0 text-start align-middle"
                                    style={{ whiteSpace: "normal" }}
                                  >
                                    {/* LEGACY_STOCK_TABLE_PRODUCT_NAME_START
                                <div className="font-weight-semibold">{item.name}</div>
                                LEGACY_STOCK_TABLE_PRODUCT_NAME_END */}
                                    <div
                                      className="font-weight-semibold"
                                      style={{
                                        whiteSpace: "normal",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {item.isTemplate ? (
                                        <span
                                          className="badge badge-info mr-1"
                                          style={{
                                            fontSize: "0.65rem",
                                            verticalAlign: "middle",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            fontWeight: "600",
                                          }}
                                        >
                                          <IntlMessages id="template.badge" />
                                        </span>
                                      ) : null}
                                      {item?.is_active === false ||
                                      item?.is_active === "false" ? (
                                        <span
                                          className="badge badge-light text-muted mr-1 border"
                                          style={{
                                            fontSize: "0.65rem",
                                            verticalAlign: "middle",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            fontWeight: "600",
                                          }}
                                        >
                                          Ngừng sử dụng
                                        </span>
                                      ) : null}
                                      {productNameWithUnit}
                                    </div>
                                  </td>
                                  {/* LEGACY_STOCK_TABLE_MAIN_CELLS_START */}
                                  {false && (
                                    <>
                                      <td className="py-0 text-start align-middle">
                                        {/* LEGACY_STOCK_TABLE_SKU_BARCODE_START
                                    <div>{item.sku || "-"}</div>
                                    <small className="text-muted">{item.barcode || "-"}</small>
                                    LEGACY_STOCK_TABLE_SKU_BARCODE_END */}
                                        {/* LEGACY_STOCK_TABLE_SKU_BARCODE_HIDE_EMPTY_START
                                    {skuDisplayValue && <div>{skuDisplayValue}</div>}
                                    {barcodeDisplayValue && (
                                      <small className="text-muted">{barcodeDisplayValue}</small>
                                    )}
                                    LEGACY_STOCK_TABLE_SKU_BARCODE_HIDE_EMPTY_END */}
                                        <div>{skuDisplayValue || "-"}</div>
                                        <small className="text-muted">
                                          {barcodeDisplayValue || "-"}
                                        </small>
                                      </td>
                                      <td className="py-0 align-middle">
                                        {formatStockQuantity(
                                          item.count || 0,
                                          item.unit,
                                        )}
                                      </td>
                                      <td className="py-0 align-middle">
                                        {formatStockNumber(
                                          item.min_stock_threshold || 0,
                                        )}
                                      </td>
                                      <td className="py-0 align-middle">
                                        {formatCurrency(item.price_new || 0)}
                                      </td>
                                      <td className="py-0 align-middle">
                                        {formatStockQuantity(
                                          item.total || 0,
                                          item.unit,
                                        )}
                                      </td>
                                      <td className="py-0 align-middle">
                                        {/* LEGACY_STOCK_TABLE_TOTAL_VALUE_DECIMAL_CURRENCY_START
                                    {formatCurrency(inventoryValue)}
                                    LEGACY_STOCK_TABLE_TOTAL_VALUE_DECIMAL_CURRENCY_END */}
                                        {formatRoundedCurrency(inventoryValue)}
                                      </td>
                                      <td className="py-0 align-middle">
                                        <Badge
                                          color={
                                            statusKey === "inactive"
                                              ? "light"
                                              : STATUS_COLOR[statusKey] ||
                                                "secondary"
                                          }
                                          className={
                                            statusKey === "inactive"
                                              ? "text-muted border"
                                              : ""
                                          }
                                          pill
                                        >
                                          <IntlMessages
                                            id={
                                              STATUS_LABEL[statusKey] ||
                                              STATUS_LABEL.in_stock
                                            }
                                          />
                                        </Badge>
                                      </td>
                                    </>
                                  )}
                                  {/* LEGACY_STOCK_TABLE_MAIN_CELLS_END */}
                                  {visibleMainColumns.map((column) => (
                                    <td
                                      key={`table-main-${item.id}-${column.key}`}
                                      className={`py-0 align-middle ${
                                        column.tableCellClass || ""
                                      }`}
                                    >
                                      {renderStockMainColumnValue(
                                        item,
                                        column.key,
                                        {
                                          statusKey,
                                          inventoryValue,
                                        },
                                      )}
                                    </td>
                                  ))}
                                  {visibleExtraColumns.map((column) => (
                                    <td
                                      key={`table-extra-${item.id}-${column.key}`}
                                      className={`py-0 align-middle d-none d-xl-table-cell ${
                                        column.tableCellClass || ""
                                      }`}
                                    >
                                      {resolveStockExtraColumnValue(
                                        item,
                                        column.key,
                                      )}
                                    </td>
                                  ))}
                                  <td className="py-0 text-center align-middle">
                                    {/* LEGACY_STOCK_ROW_ACTIONS_START
                                <div className="d-flex">
                                  ...
                                </div>
                                LEGACY_STOCK_ROW_ACTIONS_END */}
                                    <Dropdown
                                      className="d-inline-block"
                                      isOpen={openMenuStockId === item.id}
                                      toggle={() =>
                                        setOpenMenuStockId((prev) =>
                                          prev === item.id ? null : item.id,
                                        )
                                      }
                                    >
                                      <DropdownToggle
                                        color="link"
                                        className="p-0 text-muted d-inline-flex align-items-center justify-content-center"
                                        style={{
                                          textDecoration: "none",
                                          boxShadow: "none",
                                          width: 22,
                                          height: 22,
                                        }}
                                        aria-label="Thao tác"
                                      >
                                        <span
                                          style={{
                                            fontSize: "1.1rem",
                                            lineHeight: 1,
                                          }}
                                        >
                                          ⋮
                                        </span>
                                      </DropdownToggle>
                                      <DropdownMenu right>
                                        {canUpdate && (
                                          <DropdownItem
                                            onClick={() => onOpenUpdate(item)}
                                          >
                                            Sửa tồn kho
                                          </DropdownItem>
                                        )}
                                        {canUpdate && (
                                          <DropdownItem
                                            onClick={() =>
                                              onOpenUpdateCount(item)
                                            }
                                          >
                                            Nhập tồn kho
                                          </DropdownItem>
                                        )}
                                        {onOpenImportHistory && (
                                          <DropdownItem
                                            onClick={() =>
                                              onOpenImportHistory(item)
                                            }
                                          >
                                            Lịch sử nhập
                                          </DropdownItem>
                                        )}
                                        {canDelete && (
                                          <DropdownItem
                                            className="text-danger"
                                            onClick={() => onDeleteItem(item)}
                                          >
                                            Xóa
                                          </DropdownItem>
                                        )}
                                        {!onOpenImportHistory &&
                                          !canUpdate &&
                                          !canDelete && (
                                            <DropdownItem disabled>
                                              Không có thao tác
                                            </DropdownItem>
                                          )}
                                      </DropdownMenu>
                                    </Dropdown>
                                  </td>
                                  <td className="py-0 text-center align-middle">
                                    <Button
                                      color="link"
                                      className="p-0 text-muted"
                                      style={{
                                        textDecoration: "none",
                                        boxShadow: "none",
                                        width: 22,
                                        height: 22,
                                      }}
                                      onClick={() =>
                                        toggleTableRowDetail(item.id)
                                      }
                                      aria-label={
                                        isExpandedTableRow
                                          ? "Thu gọn chi tiết"
                                          : "Mở rộng chi tiết"
                                      }
                                    >
                                      <span
                                        style={{
                                          fontSize: "1rem",
                                          lineHeight: 1,
                                        }}
                                      >
                                        {isExpandedTableRow ? "▴" : "▾"}
                                      </span>
                                    </Button>
                                  </td>
                                </tr>
                                {isExpandedTableRow &&
                                  expandedDetailColumns.length > 0 && (
                                    <tr>
                                      <td
                                        colSpan={tableVisibleColumnCount}
                                        className="bg-light text-left px-3 py-2"
                                      >
                                        <Row className="mx-0">
                                          {expandedDetailColumns.map(
                                            (column) => (
                                              <Col
                                                xs="12"
                                                sm="6"
                                                className="px-2 py-1"
                                                key={`table-expand-${item.id}-${column.key}`}
                                              >
                                                <small className="text-muted d-block">
                                                  {column.label}
                                                </small>
                                                <div
                                                  className={`font-weight-semibold text-left ${
                                                    column.tableCellClass || ""
                                                  }`}
                                                >
                                                  {resolveStockExtraColumnValue(
                                                    item,
                                                    column.key,
                                                  )}
                                                </div>
                                              </Col>
                                            ),
                                          )}
                                        </Row>
                                      </td>
                                    </tr>
                                  )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <>
                    {/* LEGACY_STOCK_INLINE_CARD_LIST_START
                  Stock card list was rendered inline here. It now uses ExpandableDataCardList
                  so other pages can reuse the same card + expand + action-menu pattern.
                  LEGACY_STOCK_INLINE_CARD_LIST_END */}
                    <ExpandableDataCardList
                      data={data}
                      columns={visibleMainColumns}
                      detailColumns={expandedDetailColumns}
                      gridTemplateColumns={stockCardGridTemplate}
                      emptyMessage="Chưa có dữ liệu..."
                      selectable
                      titleHeaderLabel="Sản phẩm"
                      allSelected={allSelectedOnPage}
                      onToggleSelectAll={onToggleSelectAllCurrent}
                      selectedIds={selectedItems}
                      onToggleItem={onToggleItem}
                      expandedIds={expandedCardRowIds}
                      onToggleExpand={toggleCardRowDetail}
                      getItemId={(item) => item.id}
                      getVisualStyle={(item) => {
                        const statusKey = resolveStockStatusKey(item);
                        const cardVisualKey = resolveStockCardVisualKey(
                          item,
                          statusKey,
                        );
                        return (
                          STOCK_CARD_VISUAL_STYLE[cardVisualKey] ||
                          STOCK_CARD_VISUAL_STYLE.in_stock
                        );
                      }}
                      headerPaddingLeft={STOCK_CARD_HEADER_PADDING_LEFT}
                      headerPaddingRight={STOCK_CARD_HEADER_PADDING_RIGHT}
                      actionHeaderLabel="Khác"
                      getCardStyle={(item) =>
                        openMenuStockId === item.id
                          ? {
                              zIndex: 1050,
                              opacity: 1,
                            }
                          : null
                      }
                      getCardClassName={(item) =>
                        openMenuStockId === item.id ? "card-dropdown-open" : ""
                      }
                      renderTitle={({ item, visualStyle }) => {
                        const productNameWithUnit =
                          resolveProductNameWithUnit(item);
                        return (
                          <div
                            className="text-left"
                            style={{ minWidth: 0, whiteSpace: "normal" }}
                          >
                            <div
                              className="font-weight-semibold"
                              style={{
                                color: visualStyle.titleColor,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                lineHeight: 1.25,
                              }}
                            >
                              {item.isTemplate ? (
                                <span
                                  className="badge badge-info mr-1"
                                  style={{
                                    fontSize: "0.65rem",
                                    verticalAlign: "middle",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  <IntlMessages id="template.badge" />
                                </span>
                              ) : null}
                              {item?.is_active === false ||
                              item?.is_active === "false" ? (
                                <span
                                  className="badge badge-light text-muted mr-1 border"
                                  style={{
                                    fontSize: "0.65rem",
                                    verticalAlign: "middle",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Ngừng sử dụng
                                </span>
                              ) : null}
                              {productNameWithUnit}
                            </div>
                          </div>
                        );
                      }}
                      renderMainValue={({ item, column, visualStyle }) => {
                        const statusKey = resolveStockStatusKey(item);
                        const cardVisualKey = resolveStockCardVisualKey(
                          item,
                          statusKey,
                        );
                        const inventoryValue =
                          Number(item.count || 0) *
                            Number(item.avarage_price || item.price_new || 0) ||
                          0;
                        return renderStockMainColumnValue(item, column.key, {
                          statusKey,
                          inventoryValue,
                          isCard: true,
                          cardVisualKey,
                          cardVisualStyle: visualStyle,
                        });
                      }}
                      renderDetailValue={({ item, column }) =>
                        resolveStockExtraColumnValue(item, column.key)
                      }
                      renderActions={({
                        item,
                        isExpanded,
                        hasDetails,
                        toggleExpand,
                      }) => (
                        <>
                          {hasDetails && (
                            <Button
                              color="link"
                              className="p-0 text-muted d-inline-flex align-items-center justify-content-center"
                              style={{
                                textDecoration: "none",
                                boxShadow: "none",
                                width: 22,
                                height: 22,
                              }}
                              onClick={toggleExpand}
                              aria-label={
                                isExpanded
                                  ? "Thu gọn chi tiết"
                                  : "Mở rộng chi tiết"
                              }
                            >
                              <span style={{ fontSize: "1rem", lineHeight: 1 }}>
                                {isExpanded ? "▴" : "▾"}
                              </span>
                            </Button>
                          )}
                          <div
                            className={`d-flex justify-content-center align-items-center ${
                              hasDetails ? "ml-1" : ""
                            }`}
                            style={{ width: 32, minWidth: 32, flexShrink: 0 }}
                          >
                            <Dropdown
                              className="d-inline-block"
                              isOpen={openMenuStockId === item.id}
                              toggle={() =>
                                setOpenMenuStockId((prev) =>
                                  prev === item.id ? null : item.id,
                                )
                              }
                            >
                              <DropdownToggle
                                color="link"
                                className="p-0 text-muted d-inline-flex align-items-center justify-content-center"
                                style={{
                                  textDecoration: "none",
                                  boxShadow: "none",
                                  width: 22,
                                  height: 22,
                                }}
                                aria-label="Thao tác"
                              >
                                <span
                                  style={{ fontSize: "1.1rem", lineHeight: 1 }}
                                >
                                  ⋮
                                </span>
                              </DropdownToggle>
                              <DropdownMenu
                                right
                                positionFixed
                                modifiers={{
                                  preventOverflow: {
                                    boundariesElement: "viewport",
                                  },
                                  computeStyle: {
                                    gpuAcceleration: false,
                                  },
                                }}
                                style={{ zIndex: 2200 }}
                              >
                                {canUpdate && (
                                  <DropdownItem
                                    onClick={() => onOpenUpdate(item)}
                                  >
                                    Sửa nguyên liệu
                                  </DropdownItem>
                                )}
                                {canUpdate && (
                                  <DropdownItem
                                    onClick={() => onOpenUpdateCount(item)}
                                  >
                                    Nhập kho thêm
                                  </DropdownItem>
                                )}
                                {onOpenImportHistory && (
                                  <DropdownItem
                                    onClick={() => onOpenImportHistory(item)}
                                  >
                                    Lịch sử nhập kho
                                  </DropdownItem>
                                )}
                                {canDelete && (
                                  <DropdownItem
                                    className="text-danger"
                                    onClick={() => onDeleteItem(item)}
                                  >
                                    Xóa
                                  </DropdownItem>
                                )}
                                {!onOpenImportHistory &&
                                  !canUpdate &&
                                  !canDelete && (
                                    <DropdownItem disabled>
                                      Không có thao tác
                                    </DropdownItem>
                                  )}
                              </DropdownMenu>
                            </Dropdown>
                          </div>
                        </>
                      )}
                    />
                  </>
                )}

                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Hiển thị{" "}
                    {(page.currentPage - 1) * page.selectedPageSize + 1} -{" "}
                    {Math.min(
                      page.currentPage * page.selectedPageSize,
                      page.totalItem || 0,
                    )}{" "}
                    của {page.totalItem || 0} sản phẩm
                  </small>
                  <div className="d-flex align-items-center">
                    {/* <span className="text-muted mr-2">/ trang</span> */}
                    <UncontrolledDropdown className="d-inline-block mr-3">
                      <DropdownToggle caret color="outline-dark" size="xs">
                        {page.selectedPageSize}
                      </DropdownToggle>
                      <DropdownMenu right>
                        {(page.pageSizes || [12, 24]).map((size) => (
                          <DropdownItem
                            key={size}
                            onClick={() => onChangePageSize(size)}
                          >
                            {size}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                    <Pagination
                      currentPage={page.currentPage}
                      totalPage={page.totalPage}
                      onChangePage={onChangePage}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" className="mb-3">
            <Card className="mb-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">Tổng quan tồn kho</h5>
                    <small className="text-muted">
                      {overviewMode === "item"
                        ? `Theo mặt hàng (${formatStockNumber(summary.total_items || 0)} SP)`
                        : `Theo số lượng tồn (${formatStockNumber(summary.total_stock || 0)} tồn)`}
                    </small>
                  </div>
                  <div
                    className="btn-group btn-group-sm"
                    role="group"
                    aria-label="Chế độ xem tổng quan"
                  >
                    <button
                      type="button"
                      className={`btn btn-xs ${
                        overviewMode === "item"
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      style={{
                        fontSize: "0.72rem",
                        padding: "2px 7px",
                        borderRadius: "4px 0 0 4px",
                      }}
                      onClick={() => setOverviewMode("item")}
                    >
                      Mặt hàng
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs ${
                        overviewMode === "quantity"
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      style={{
                        fontSize: "0.72rem",
                        padding: "2px 7px",
                        borderRadius: "0 4px 4px 0",
                      }}
                      onClick={() => setOverviewMode("quantity")}
                    >
                      Số lượng
                    </button>
                  </div>
                </div>
                {isLoadingDashboard ? (
                  <div className="text-muted">Đang tải...</div>
                ) : (
                  <>
                    <Row className="no-gutters align-items-center">
                      <Col
                        xs="12"
                        lg="5"
                        className="mb-3 mb-lg-0 d-flex justify-content-center"
                      >
                        <div
                          style={{
                            width: "100%",
                            maxWidth: 170,
                            aspectRatio: "1 / 1",
                            borderRadius: "50%",
                            background: overviewConicGradient,
                            position: "relative",
                            flexShrink: 0,
                          }}
                        >
                          <div
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{
                              position: "absolute",
                              top: 18,
                              right: 18,
                              bottom: 18,
                              left: 18,
                              borderRadius: "50%",
                              backgroundColor: "#ffffff",
                              boxShadow: "inset 0 0 0 1px #f1f3f5",
                            }}
                          >
                            <div className="h4 mb-0">
                              {overviewMode === "item"
                                ? formatStockNumber(summary.total_items || 0)
                                : formatStockNumber(summary.total_stock || 0)}
                            </div>
                            <small className="text-muted">
                              {overviewMode === "item"
                                ? "Mặt hàng"
                                : "Tổng tồn"}
                            </small>
                          </div>
                        </div>
                      </Col>
                      <Col xs="12" lg="7" className="pl-lg-2">
                        {overviewRows.map((overview) => {
                          const isActiveFilter =
                            filters?.status === overview.key;
                          return (
                            <div
                              key={overview.key}
                              className={`d-flex justify-content-between align-items-center mb-2 px-1 py-1 rounded ${
                                isActiveFilter
                                  ? "bg-light font-weight-bold"
                                  : ""
                              }`}
                              style={{
                                cursor: "pointer",
                                transition: "background-color 0.15s ease",
                              }}
                              onClick={() => {
                                const nextStatus =
                                  filters?.status === overview.key
                                    ? ""
                                    : overview.key;
                                onFilterChange("status", nextStatus);
                              }}
                              title={`Click để lọc danh sách theo: ${overview.label}`}
                            >
                              <div
                                className="d-flex align-items-center pr-2"
                                style={{ minWidth: 0 }}
                              >
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: overview.color,
                                    marginRight: 8,
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  className={`text-small text-truncate ${
                                    isActiveFilter
                                      ? "text-primary font-weight-bold"
                                      : "text-muted"
                                  }`}
                                >
                                  {overview.label}
                                </span>
                              </div>
                              <div className="text-right text-nowrap pl-1">
                                <span className="text-small font-weight-semibold">
                                  {formatStockNumber(overview.count)} (
                                  {Number(overview.percent || 0).toFixed(1)}%)
                                </span>
                                {overviewMode === "item" &&
                                  overview.quantity > 0 && (
                                    <small
                                      className="text-muted d-block"
                                      style={{
                                        fontSize: "0.7rem",
                                        lineHeight: 1.1,
                                      }}
                                    >
                                      {formatStockNumber(overview.quantity)} tồn
                                    </small>
                                  )}
                                {overviewMode === "quantity" && (
                                  <small
                                    className="text-muted d-block"
                                    style={{
                                      fontSize: "0.7rem",
                                      lineHeight: 1.1,
                                    }}
                                  >
                                    {formatStockNumber(overview.itemCount)} SP
                                  </small>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </Col>
                    </Row>
                  </>
                )}
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-3">Cảnh báo tồn kho</h5>
                {alertRows.map((alert, index) => (
                  <div
                    key={alert.key}
                    className={`d-flex align-items-center ${index < alertRows.length - 1 ? "mb-2" : ""}`}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center mr-2"
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: alert.bgColor,
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className={alert.icon}
                        style={{ color: alert.color, fontSize: "0.75rem" }}
                      />
                    </div>
                    <span className="text-muted">
                      <strong className="text-body">
                        {formatStockNumber(alert.count)}
                      </strong>{" "}
                      sản phẩm {alert.text}
                    </span>
                  </div>
                ))}
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <h5 className="mb-2">Nhập kho gần đây</h5>
                {recentImports.length === 0 ? (
                  <div className="text-muted">Chưa có dữ liệu</div>
                ) : (
                  recentImports.slice(0, 3).map((item) => (
                    <div key={item.id} className="mt-2 border-top">
                      <div className="d-flex justify-content-between align-items-start mb-1 mt-2">
                        <div className="font-weight-semibold pr-2">
                          {item.stock_name || "-"}
                        </div>
                        <small className="text-primary text-nowrap font-weight-semibold">
                          {formatStockQuantity(item.quantity, item.unit)}
                        </small>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted d-flex align-items-center">
                          <span className="mr-2">{item.stock_sku || "-"}</span>
                          <span>
                            {item.date} {item.time}
                          </span>
                        </small>
                        <small className="text-success text-nowrap font-weight-semibold">
                          +{formatCurrency(item.total_value)}
                        </small>
                      </div>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h5 className="mb-3">Thao tác nhanh</h5>
                {/* QUICK_ACTIONS_LEGACY_START
              <div className="d-flex flex-column">
                <Link className="btn btn-outline-primary btn-sm mb-2" to="/app/pages/warehouse-import">
                  Nhập kho
                </Link>
                <Link className="btn btn-outline-primary btn-sm mb-2" to="/app/pages/warehouse-export">
                  Xuất kho
                </Link>
                <Link className="btn btn-outline-primary btn-sm mb-2" to="/app/pages/warehouse-transfer">
                  Chuyển kho
                </Link>
                <Link className="btn btn-outline-primary btn-sm" to="/app/pages/inventory-audit">
                  Kiểm kê kho
                </Link>
              </div>
              QUICK_ACTIONS_LEGACY_END */}

                <Row className="mx-n1">
                  {quickActions.map((action) => (
                    <Col key={action.key} xs="12" sm="6" className="px-1 mb-2">
                      <Link
                        to={action.to}
                        className="d-flex align-items-start h-100 border rounded p-2"
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-center mr-2"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            backgroundColor: action.iconBg,
                            flexShrink: 0,
                          }}
                        >
                          <i
                            className={action.icon}
                            style={{
                              color: action.iconColor,
                              fontSize: "0.82rem",
                            }}
                          />
                        </div>
                        <div className="w-100">
                          <div className="font-weight-semibold text-body mb-0">
                            {action.title}
                          </div>
                          <small className="text-muted d-block">
                            {action.subtitle}
                          </small>
                        </div>
                      </Link>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default StockDashboardLayout;
